import type { FunctionComponent } from 'preact';
import { useMemo } from 'preact/hooks';

import { IsolatedKey } from 'Addons/fitmentSearch/isolatedKeys';
import { createSelectRepeater, SelectContextProvider } from 'Components/smallComponents/select';
import {
  createFitmentSearchRequestSelectionSelector,
  fitmentSearchBaseFieldsSelector,
  garageSizeSelector,
} from 'Core/selectors/fitmentSearch/index';
import { useSelector } from 'Core/store';
import fitmentSearchConfig from 'Models/uiConfig/fitmentSearchConfig';
import { equalBy } from 'Utils/array';
import { cloneSafe } from 'Utils/components';
import safeTemplateCall from 'Utils/safeTemplateCall';
import t from 'Utils/translate';

import type { InnerProps as Props } from './vehicleDropdowns';

const stub = () => {
  // stub
};

const StubDropdowns: FunctionComponent<Props> = ({
  template,
  name,
  aboutToChange,
  appendedClasses,
  collapsing,
  fields,
  isolatedKey,
  isVehicleSelected,
  openDialog,
  rootRef,
  useNativeDropdown,
  useSearchableDropdown,
  autoSyncVisualization,
}) => {
  const fitmentBaseFields = useSelector(fitmentSearchBaseFieldsSelector);
  const vehicleListSize = useSelector(garageSizeSelector);
  const selectFields = !fields || equalBy(fitmentBaseFields, fields, (v) => v) ? fitmentBaseFields : [];
  const vehicle = useVehicle(isolatedKey, selectFields);

  const selects = createSelectRepeater(
    selectFields.map((field, i) => {
      const title = t(`facet_title_${field.replaceAll(' ', '_')}`) || field;

      return {
        entries: [{ value: vehicle[field] || title, term: '', selected: field in vehicle }],
        extraClassName: 'cm_vehicle-widget_select',
        disabled: i !== 0,
        loading: i === 0,
        hideNullOption: true,
        field,
        title,
        key: field,
        index: i,
        useNativeDropdown: !useSearchableDropdown && useNativeDropdown,
        isSearchable: useSearchableDropdown,
        onChange: () => {
          /* stub */
        },
      };
    }),
  );

  const component = safeTemplateCall(
    template,
    {
      name,
      aboutToChange,
      selects,
      vehicle: {},
      vehicleString: '',
      vehicleBaseValues: [],
      vehicleExtraValues: [],
      vehicleListSize,
      allowToSetVehicle: false,
      allowToDiscardVehicle: false,
      changeVehicle: stub,
      selectVehicle: stub,
      discardVehicle: stub,
      facetToggle: () => null,
      discardExtraFields: stub,
      discardFields: stub,
      fitmentSearchTitle: fitmentSearchConfig.fitmentSearchTitle,
      template: 'active',
      isVehicleSelected,
      selection: {},
      ...collapsing,
      isVehicleSelectionForced: false,
      isAllFieldsSelected: false,
      openDialog,
      autoSyncVisualization,
    },
    'StubDropdowns',
  );

  return <SelectContextProvider>{cloneSafe(component, rootRef, { appendedClasses })}</SelectContextProvider>;
};

function useVehicle(isolatedKey: IsolatedKey, fields: string[]) {
  const selector = useMemo(() => createFitmentSearchRequestSelectionSelector(isolatedKey), [isolatedKey]);

  const selection = useSelector(selector);

  return useMemo(() => {
    return Object.fromEntries(
      fields
        .map((field) => [field, selection.find((v) => v.field === field)?.value])
        .filter(([, value]) => value) as [string, string][],
    );
  }, [selection, fields]);
}

export default StubDropdowns;
